/**
 * RequestProfiler - система профілювання REST запитів для dev режиму
 * Збирає інформацію про всі HTTP запити для аналізу та оптимізації
 */

import type { Options } from 'ky'
import { makeAutoObservable } from 'mobx'

/**
 * Інформація про один HTTP запит
 */
export interface RequestProfile {
    /** Унікальний ID запиту */
    id: string
    /** HTTP метод */
    method: string
    /** Повний URL запиту */
    url: string
    /** Базовий URL (без query параметрів) */
    baseUrl: string
    /** Шлях URL (без домену) */
    pathname: string
    /** Заголовки запиту */
    headers: Record<string, string>
    /** Тіло запиту (якщо є) */
    body?: any
    /** Розмір тіла запиту в байтах */
    requestSize?: number
    /** Query параметри */
    searchParams?: Record<string, any>
    /** Час початку запиту */
    startTime: number
    /** Час завершення запиту */
    endTime?: number
    /** Тривалість в мс */
    duration?: number
    /** HTTP статус код */
    statusCode?: number
    /** Текст статусу */
    statusText?: string
    /** Розмір відповіді в байтах */
    responseSize?: number
    /** Заголовки відповіді */
    responseHeaders?: Record<string, string>
    /** Тіло відповіді (обмежено) */
    responseBody?: any
    /** Чи була помилка */
    hasError: boolean
    /** Повідомлення про помилку */
    errorMessage?: string
    /** Стек помилки */
    errorStack?: string
    /** Налаштування запиту */
    settings?: Record<string, any>
    /** Теги для категоризації */
    tags?: string[]
    /** Чи було використано кеш */
    fromCache?: boolean
    /** Чи було повторне виконання (retry) */
    wasRetried?: boolean
    /** Кількість спроб */
    retryCount?: number
}

/**
 * Статистика запитів
 */
export interface RequestStats {
    /** Загальна кількість запитів */
    total: number
    /** Успішні запити (2xx) */
    successful: number
    /** Помилки клієнта (4xx) */
    clientErrors: number
    /** Помилки сервера (5xx) */
    serverErrors: number
    /** Запити в процесі */
    pending: number
    /** Середня тривалість запиту */
    avgDuration: number
    /** Найповільніший запит */
    slowestDuration: number
    /** Найшвидший запит */
    fastestDuration: number
    /** Загальний обсяг даних (байти) */
    totalDataSize: number
}

/**
 * Клас для профілювання REST запитів
 */
class RequestProfiler {
    /** Масив всіх запитів */
    requests: RequestProfile[] = []

    /** Максимальна кількість запитів в історії */
    maxRequests = 1000

    /** Чи увімкнено профілювання */
    enabled = false

    /** Лічильник для генерації ID */
    private requestCounter = 0

    constructor() {
        makeAutoObservable(this)

        // Автоматично вмикаємо в dev режимі
        const env = (import.meta as any).env
        if (env.DEV) {
            this.enable()
        }
    }

    /**
     * Увімкнути профілювання
     */
    enable(): void {
        this.enabled = true
    }

    /**
     * Вимкнути профілювання
     */
    disable(): void {
        this.enabled = false
    }

    /**
     * Очистити історію запитів
     */
    clear(): void {
        this.requests = []
        this.requestCounter = 0
    }

    /**
     * Позначити запит як використаний з кешу
     */
    markAsCached(id: string): void {
        const profile = this.requests.find((r) => r.id === id)
        if (profile) {
            profile.fromCache = true
        }
    }

    /**
     * Позначити запит як повторну спробу
     */
    markAsRetried(id: string): void {
        const profile = this.requests.find((r) => r.id === id)
        if (profile) {
            profile.wasRetried = true
            profile.retryCount = (profile.retryCount || 0) + 1
        }
    }

    /**
     * Додати тег до запиту
     */
    addTag(id: string, tag: string): void {
        const profile = this.requests.find((r) => r.id === id)
        if (profile) {
            if (!profile.tags) {
                profile.tags = []
            }
            if (!profile.tags.includes(tag)) {
                profile.tags.push(tag)
            }
        }
    }

    /**
     * Почати відстеження запиту
     */
    startRequest(method: string, url: string, options: Options, settings?: Record<string, any>): string {
        if (!this.enabled) {
            return ''
        }

        const id = `req_${++this.requestCounter}_${Date.now()}`

        // Парсимо URL для отримання деталей
        let baseUrl = url
        let pathname = ''
        try {
            const urlObj = new URL(url)
            baseUrl = `${urlObj.origin}${urlObj.pathname}`
            pathname = urlObj.pathname
        } catch {
            // Якщо не вдалося розпарсити - використовуємо як є
            pathname = url.split('?')[0]
            baseUrl = pathname
        }

        // Витягуємо заголовки
        const headers: Record<string, string> = {}
        if (options.headers) {
            if (options.headers instanceof Headers) {
                options.headers.forEach((value, key) => {
                    headers[key] = value
                })
            } else if (typeof options.headers === 'object') {
                Object.assign(headers, options.headers)
            }
        }

        // Витягуємо searchParams
        let searchParams: Record<string, any> | undefined
        if (options.searchParams) {
            if (options.searchParams instanceof URLSearchParams) {
                searchParams = {}
                options.searchParams.forEach((value, key) => {
                    searchParams![key] = value
                })
            } else if (typeof options.searchParams === 'object') {
                searchParams = { ...options.searchParams }
            }
        }

        // Витягуємо body та обчислюємо розмір
        let body: any
        let requestSize: number | undefined
        if (options.json) {
            body = options.json
            try {
                requestSize = new Blob([JSON.stringify(options.json)]).size
            } catch {
                // Ігноруємо помилки
            }
        } else if (options.body) {
            // Для FormData та інших типів просто зберігаємо тип
            if (options.body instanceof FormData) {
                body = '[FormData]'
                // Спробуємо підрахувати розмір
                try {
                    let size = 0
                    options.body.forEach((value) => {
                        if (typeof value === 'string') {
                            size += value.length
                        } else if (value instanceof Blob) {
                            size += value.size
                        }
                    })
                    requestSize = size
                } catch {
                    // Ігноруємо
                }
            } else if (options.body instanceof Blob) {
                body = '[Blob]'
                requestSize = options.body.size
            } else if (typeof options.body === 'string') {
                try {
                    body = JSON.parse(options.body)
                    requestSize = new Blob([options.body]).size
                } catch {
                    body = options.body
                    requestSize = new Blob([options.body]).size
                }
            } else {
                body = options.body
            }
        }

        // Автоматичне додавання тегів
        const tags: string[] = []
        if (pathname.includes('/api/')) tags.push('API')
        if (pathname.includes('/auth/')) tags.push('Auth')
        if (pathname.includes('/user')) tags.push('User')
        if (method === 'GET') tags.push('Read')
        if (['POST', 'PUT', 'PATCH'].includes(method)) tags.push('Write')
        if (method === 'DELETE') tags.push('Delete')

        const profile: RequestProfile = {
            id,
            method: method.toUpperCase(),
            url,
            baseUrl,
            pathname,
            headers,
            body,
            requestSize,
            searchParams,
            startTime: Date.now(),
            hasError: false,
            settings,
            tags,
            fromCache: false,
            wasRetried: false,
            retryCount: 0,
        }

        this.requests.push(profile)

        // Обмежуємо кількість запитів в історії
        if (this.requests.length > this.maxRequests) {
            this.requests.shift()
        }

        return id
    }

    /**
     * Завершити відстеження запиту з успіхом
     */
    endRequest(id: string, response: Response, responseData?: any): void {
        if (!this.enabled || !id) {
            return
        }

        const profile = this.requests.find((r) => r.id === id)
        if (!profile) {
            return
        }

        profile.endTime = Date.now()
        profile.duration = profile.endTime - profile.startTime
        profile.statusCode = response.status
        profile.statusText = response.statusText

        // Витягуємо заголовки відповіді
        const responseHeaders: Record<string, string> = {}
        response.headers.forEach((value, key) => {
            responseHeaders[key] = value
        })
        profile.responseHeaders = responseHeaders

        // Розмір відповіді
        const contentLength = response.headers.get('content-length')
        if (contentLength) {
            profile.responseSize = parseInt(contentLength, 10)
        }

        // Зберігаємо частину відповіді (обмежено для економії пам'яті)
        if (responseData !== undefined) {
            profile.responseBody = this.limitResponseBody(responseData)
        }
    }

    /**
     * Завершити відстеження запиту з помилкою
     */
    endRequestWithError(id: string, error: Error | unknown): void {
        if (!this.enabled || !id) {
            return
        }

        const profile = this.requests.find((r) => r.id === id)
        if (!profile) {
            return
        }

        profile.endTime = Date.now()
        profile.duration = profile.endTime - profile.startTime
        profile.hasError = true

        if (error instanceof Error) {
            profile.errorMessage = error.message
            profile.errorStack = error.stack

            // Для HTTPError з Ky
            if ('response' in error && error.response instanceof Response) {
                profile.statusCode = (error.response as Response).status
                profile.statusText = (error.response as Response).statusText
            }
        } else {
            profile.errorMessage = String(error)
        }
    }

    /**
     * Отримати статистику запитів
     */
    getStats(): RequestStats {
        const completed = this.requests.filter((r) => r.endTime !== undefined)
        const pending = this.requests.filter((r) => r.endTime === undefined)

        const successful = completed.filter(
            (r) => !r.hasError && r.statusCode && r.statusCode >= 200 && r.statusCode < 300
        )

        const clientErrors = completed.filter((r) => r.statusCode && r.statusCode >= 400 && r.statusCode < 500)

        const serverErrors = completed.filter((r) => r.statusCode && r.statusCode >= 500)

        const durations = completed.filter((r) => r.duration !== undefined).map((r) => r.duration!)

        const avgDuration = durations.length > 0 ? durations.reduce((sum, d) => sum + d, 0) / durations.length : 0

        const slowestDuration = durations.length > 0 ? Math.max(...durations) : 0

        const fastestDuration = durations.length > 0 ? Math.min(...durations) : 0

        const totalDataSize = completed
            .filter((r) => r.responseSize !== undefined)
            .reduce((sum, r) => sum + (r.responseSize || 0), 0)

        return {
            total: this.requests.length,
            successful: successful.length,
            clientErrors: clientErrors.length,
            serverErrors: serverErrors.length,
            pending: pending.length,
            avgDuration: Math.round(avgDuration),
            slowestDuration,
            fastestDuration,
            totalDataSize,
        }
    }

    /**
     * Отримати всі запити
     */
    getAllRequests(): RequestProfile[] {
        return [...this.requests].reverse() // Найновіші спочатку
    }

    /**
     * Отримати запити за критеріями
     */
    getRequestsByMethod(method: string): RequestProfile[] {
        return this.requests.filter((r) => r.method === method.toUpperCase())
    }

    /**
     * Отримати запити з помилками
     */
    getErrorRequests(): RequestProfile[] {
        return this.requests.filter((r) => r.hasError)
    }

    /**
     * Отримати повільні запити (більше ніж threshold мс)
     */
    getSlowRequests(threshold = 1000): RequestProfile[] {
        return this.requests.filter((r) => r.duration !== undefined && r.duration > threshold)
    }

    /**
     * Отримати запити за тегом
     */
    getRequestsByTag(tag: string): RequestProfile[] {
        return this.requests.filter((r) => r.tags?.includes(tag))
    }

    /**
     * Отримати запити за URL патерном
     */
    getRequestsByUrlPattern(pattern: string | RegExp): RequestProfile[] {
        if (typeof pattern === 'string') {
            return this.requests.filter((r) => r.url.includes(pattern))
        }
        return this.requests.filter((r) => pattern.test(r.url))
    }

    /**
     * Отримати запити за статус кодом
     */
    getRequestsByStatus(statusCode: number): RequestProfile[] {
        return this.requests.filter((r) => r.statusCode === statusCode)
    }

    /**
     * Отримати запити за діапазоном статус кодів
     */
    getRequestsByStatusRange(min: number, max: number): RequestProfile[] {
        return this.requests.filter((r) => r.statusCode !== undefined && r.statusCode >= min && r.statusCode <= max)
    }

    /**
     * Отримати найбільші запити за розміром відповіді
     */
    getLargestRequests(limit = 10): RequestProfile[] {
        return [...this.requests]
            .filter((r) => r.responseSize !== undefined)
            .sort((a, b) => (b.responseSize || 0) - (a.responseSize || 0))
            .slice(0, limit)
    }

    /**
     * Отримати статистику за endpoint (групування за pathname)
     */
    getEndpointStats(): Map<
        string,
        {
            count: number
            avgDuration: number
            minDuration: number
            maxDuration: number
            successRate: number
            totalSize: number
        }
    > {
        const stats = new Map()

        this.requests.forEach((r) => {
            const endpoint = r.pathname
            if (!stats.has(endpoint)) {
                stats.set(endpoint, {
                    count: 0,
                    totalDuration: 0,
                    durations: [] as number[],
                    successCount: 0,
                    totalSize: 0,
                })
            }

            const stat = stats.get(endpoint)
            stat.count++

            if (r.duration !== undefined) {
                stat.totalDuration += r.duration
                stat.durations.push(r.duration)
            }

            if (r.statusCode && r.statusCode >= 200 && r.statusCode < 300) {
                stat.successCount++
            }

            if (r.responseSize) {
                stat.totalSize += r.responseSize
            }
        })

        // Обчислюємо фінальні метрики
        const result = new Map()
        stats.forEach((stat: any, endpoint: string) => {
            result.set(endpoint, {
                count: stat.count,
                avgDuration: stat.durations.length > 0 ? Math.round(stat.totalDuration / stat.durations.length) : 0,
                minDuration: stat.durations.length > 0 ? Math.min(...stat.durations) : 0,
                maxDuration: stat.durations.length > 0 ? Math.max(...stat.durations) : 0,
                successRate: stat.count > 0 ? Math.round((stat.successCount / stat.count) * 100) : 0,
                totalSize: stat.totalSize,
            })
        })

        return result
    }

    /**
     * Отримати статистику за методами
     */
    getMethodStats(): Map<
        string,
        {
            count: number
            avgDuration: number
            successCount: number
            errorCount: number
        }
    > {
        const stats = new Map()

        this.requests.forEach((r) => {
            if (!stats.has(r.method)) {
                stats.set(r.method, {
                    count: 0,
                    totalDuration: 0,
                    successCount: 0,
                    errorCount: 0,
                })
            }

            const stat = stats.get(r.method)
            stat.count++

            if (r.duration !== undefined) {
                stat.totalDuration += r.duration
            }

            if (r.hasError || (r.statusCode && r.statusCode >= 400)) {
                stat.errorCount++
            } else if (r.statusCode && r.statusCode >= 200 && r.statusCode < 300) {
                stat.successCount++
            }
        })

        // Обчислюємо середню тривалість
        const result = new Map()
        stats.forEach((stat: any, method: string) => {
            result.set(method, {
                count: stat.count,
                avgDuration: stat.count > 0 ? Math.round(stat.totalDuration / stat.count) : 0,
                successCount: stat.successCount,
                errorCount: stat.errorCount,
            })
        })

        return result
    }

    /**
     * Експортувати всі запити як JSON
     */
    exportToJson(): string {
        return JSON.stringify(this.requests, null, 2)
    }

    /**
     * Експортувати статистику як JSON
     */
    exportStatsToJson(): string {
        const stats = this.getStats()
        const endpointStats = this.getEndpointStats()
        const methodStats = this.getMethodStats()

        return JSON.stringify(
            {
                general: stats,
                endpoints: Array.from(endpointStats.entries()).map(([endpoint, stat]) => ({
                    endpoint,
                    ...stat,
                })),
                methods: Array.from(methodStats.entries()).map(([method, stat]) => ({
                    method,
                    ...stat,
                })),
                timestamp: new Date().toISOString(),
            },
            null,
            2
        )
    }

    /**
     * Знайти дублікати запитів (однакові URL та метод протягом короткого часу)
     */
    findDuplicateRequests(timeWindowMs = 1000): RequestProfile[][] {
        const groups = new Map<string, RequestProfile[]>()

        this.requests.forEach((r) => {
            const key = `${r.method}:${r.baseUrl}`
            if (!groups.has(key)) {
                groups.set(key, [])
            }
            groups.get(key)!.push(r)
        })

        const duplicates: RequestProfile[][] = []

        groups.forEach((requests) => {
            if (requests.length < 2) return

            // Сортуємо за часом
            requests.sort((a, b) => a.startTime - b.startTime)

            // Знаходимо запити в межах timeWindow
            for (let i = 0; i < requests.length - 1; i++) {
                const group: RequestProfile[] = [requests[i]]

                for (let j = i + 1; j < requests.length; j++) {
                    if (requests[j].startTime - requests[i].startTime <= timeWindowMs) {
                        group.push(requests[j])
                    } else {
                        break
                    }
                }

                if (group.length > 1) {
                    duplicates.push(group)
                    i += group.length - 1 // Пропускаємо оброблені запити
                }
            }
        })

        return duplicates
    }

    /**
     * Отримати timeline запитів (для візуалізації)
     */
    getTimeline(bucketSizeMs = 1000): Array<{
        timestamp: number
        requests: number
        avgDuration: number
        errors: number
    }> {
        if (this.requests.length === 0) return []

        const minTime = Math.min(...this.requests.map((r) => r.startTime))
        const maxTime = Math.max(...this.requests.map((r) => r.endTime || r.startTime))

        const buckets = new Map<
            number,
            {
                requests: number
                totalDuration: number
                errors: number
            }
        >()

        this.requests.forEach((r) => {
            const bucketKey = Math.floor((r.startTime - minTime) / bucketSizeMs) * bucketSizeMs + minTime

            if (!buckets.has(bucketKey)) {
                buckets.set(bucketKey, { requests: 0, totalDuration: 0, errors: 0 })
            }

            const bucket = buckets.get(bucketKey)!
            bucket.requests++

            if (r.duration) {
                bucket.totalDuration += r.duration
            }

            if (r.hasError || (r.statusCode && r.statusCode >= 400)) {
                bucket.errors++
            }
        })

        return Array.from(buckets.entries())
            .map(([timestamp, data]) => ({
                timestamp,
                requests: data.requests,
                avgDuration: data.requests > 0 ? Math.round(data.totalDuration / data.requests) : 0,
                errors: data.errors,
            }))
            .sort((a, b) => a.timestamp - b.timestamp)
    }

    /**
     * Обмежити розмір тіла відповіді для збереження
     */
    private limitResponseBody(data: any): any {
        // Якщо це масив - зберігаємо тільки перші 10 елементів
        if (Array.isArray(data)) {
            return data.slice(0, 10).map((item) => this.limitObject(item))
        }

        return this.limitObject(data)
    }

    /**
     * Обмежити розмір об'єкта
     */
    private limitObject(obj: any, maxDepth = 3, currentDepth = 0): any {
        if (currentDepth >= maxDepth) {
            return '[Обрізано]'
        }

        if (obj === null || obj === undefined) {
            return obj
        }

        if (typeof obj !== 'object') {
            return obj
        }

        if (Array.isArray(obj)) {
            return obj.slice(0, 5).map((item) => this.limitObject(item, maxDepth, currentDepth + 1))
        }

        const limited: any = {}
        let count = 0
        const maxKeys = 20

        for (const key in obj) {
            if (count >= maxKeys) {
                limited['...'] = `[Ще ${Object.keys(obj).length - maxKeys} полів]`
                break
            }
            limited[key] = this.limitObject(obj[key], maxDepth, currentDepth + 1)
            count++
        }

        return limited
    }
}

/** Глобальний екземпляр профайлера */
export const requestProfiler = new RequestProfiler()
